import React from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Button, Dropdown, Grid } from 'antd';

import GroupHeader from '../common/GroupHeader';
import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import { buildActionMenu } from '../common/actionMenu';
import { KEBAB, PLUS, REFRESH } from '../icons';
import { missingAddOns, rowWords } from '../../utils/packageInstalls';
import { addExampleData, canReviewUpdate, exampleDataAddedNote } from '../../services/packages';

/** Does the package carry examples a manager may add to a collection that holds it (keepr-api KPR-365)? Never a collection package's core items. */
const carriesExamples = pkg => Number(pkg?.contents?.items) > 0 && pkg?.contents?.itemsRole === 'demo';

/**
 * One collection that holds the package: its record badge, its version and
 * the add-ons it holds, and what a person does from here — *Open* the
 * collection, *Add an add-on* (managers only, and only while the bundle has
 * one the collection lacks) and *Add example data* (managers only, when the
 * package carries examples; keepr-api KPR-365). At most two labeled buttons
 * (Open and the first that applies); the rest behind ⋯, and on a phone all of
 * them fold into one ⋯ (docs/platform-standards.md §3).
 */
function InstalledInRow({ pkg, entry, phone, onAddAddOn, onReviewUpdate }) {
	const navigate = useNavigate();
	const { message, modal } = App.useApp();
	const open = () => navigate(`/collections/${entry.collectionId}`);
	const canAdd = entry.canManage === true && missingAddOns(pkg, entry).length > 0;
	const canAddExamples = entry.canManage === true && carriesExamples(pkg);
	const addOn = () => onAddAddOn(entry.collectionId);
	const addExamples = () => modal.confirm({
		title: `Add ${pkg.name || 'the package'}'s example data to ${entry.name || 'this collection'}?`,
		content: 'The examples not there yet land on the package\'s cards, marked Demo. Remove demo data in the collection\'s menu takes them out again.',
		okText: 'Add examples',
		onOk: async () => {
			try {
				const answer = await addExampleData(entry.collectionId, pkg.key);
				message.success(exampleDataAddedNote(answer));
				const said = Array.isArray(answer?.warnings) ? answer.warnings.find(w => w?.message) : null;
				if (said) message.warning(said.message);
			} catch (e) {
				message.error(e?.response?.data?.message || 'The example data could not be added.');
			}
		},
	});
	// KPR-458: a manager's collection behind the published version reviews its update
	// (even one set aside with Ignore this update: the chip is off, the door stays).
	const canReview = entry.canManage === true && typeof onReviewUpdate === 'function' && canReviewUpdate({ version: entry.version, publishedVersion: pkg?.version, retired: pkg?.status === 'retired' });
	const extras = [
		canReview ? { key: 'review-update', label: 'Review update', onClick: () => onReviewUpdate(entry), icon: <REFRESH /> } : null,
		canAdd ? { key: 'add-on', label: 'Add an add-on', onClick: addOn } : null,
		canAddExamples ? { key: 'examples', label: 'Add example data', onClick: addExamples } : null,
	].filter(Boolean);
	const menu = buildActionMenu([
		{ key: 'open', label: 'Open', group: 'navigate', glyph: 'open', onClick: open },
		...extras.map(x => ({ ...x, group: 'record', icon: x.icon || <PLUS /> })),
	]);
	// Beside Open, one labeled secondary; anything past it behind ⋯.
	const [shown, ...folded] = extras;
	const foldedMenu = folded.length ? buildActionMenu(folded.map(x => ({ ...x, group: 'record', icon: x.icon || <PLUS /> }))) : null;
	return (
		<li className="installed-in__row">
			<RecordIdentity
				badge={<RecordBadge kind="collection" record={{ icon: entry.icon, color: entry.color, name: entry.name }} title={entry.name || 'Collection'} size="sm" to={`/collections/${entry.collectionId}`} />}
				below={rowWords(entry, pkg) || null}
				actions={phone ? (
					<Dropdown trigger={['click']} placement="bottomRight" menu={menu}>
						<Button type="text" aria-label={`${entry.name || 'Collection'} actions`} icon={<KEBAB className="medium-icon" />} />
					</Dropdown>
				) : (
					<span className="installed-in__actions">
						<Button onClick={open}>Open</Button>
						{shown ? <Button onClick={shown.onClick}>{shown.label}</Button> : null}
						{foldedMenu ? (
							<Dropdown trigger={['click']} placement="bottomRight" menu={foldedMenu}>
								<Button type="text" aria-label={`More for ${entry.name || 'Collection'}`} icon={<KEBAB className="medium-icon" />} />
							</Dropdown>
						) : null}
					</span>
				)}
			/>
		</li>
	);
}

/**
 * *In your collections* on a package's page (keepr-api KPR-364): one row for
 * each collection the person reads that holds it, from the install record.
 * Nothing at all for a package they do not hold. *Review update* (KPR-458) is
 * the first action of a row the person manages that is behind.
 */
export default function InstalledIn({ pkg, onAddAddOn, onReviewUpdate }) {
	const screens = Grid.useBreakpoint();
	const entries = Array.isArray(pkg?.installedIn) ? pkg.installedIn : [];
	if (!entries.length) return null;
	return (
		<section className="package-page__section installed-in" aria-label="In your collections">
			<GroupHeader as="h2" label="In your collections" count={entries.length} countLabel="collection" />
			<ul className="installed-in__list">
				{entries.map(entry => (
					<InstalledInRow key={entry.collectionId} pkg={pkg} entry={entry} phone={screens.md === false} onAddAddOn={onAddAddOn} onReviewUpdate={onReviewUpdate} />
				))}
			</ul>
		</section>
	);
}
